import { useCallback, RefObject } from 'react';
import type { MentionAutocompleteHandle } from '../../MentionAutocomplete';
import type { MinimalDocument } from '../../../../types';
import type { MentionFilters } from '../../MentionTypes';
import logger from '../../../../utils/simpleLogger';

/**
 * Hook for input event handlers
 *
 * Responsibilities:
 * - Handle keydown events (Enter to send, Delete/Backspace to remove docs)
 * - Handle document chip clicks
 */

interface UseInputHandlersProps {
  inputMessage: string;
  selectedMinimalDocuments: MinimalDocument[];
  selectedDocuments: string[];
  contextFilters: MentionFilters;
  mentionAutocompleteRef: RefObject<MentionAutocompleteHandle | null>;
  deselectContextDocuments: (ids: string[]) => void;
  setContextFilters: (filters: MentionFilters) => void;
  sendMessage: () => void;
}

export function useInputHandlers({
  inputMessage,
  selectedMinimalDocuments,
  selectedDocuments,
  contextFilters,
  mentionAutocompleteRef,
  deselectContextDocuments,
  setContextFilters,
  sendMessage,
}: UseInputHandlersProps) {
  /**
   * Handle keydown events on the input
   * - Delegates to mention autocomplete first
   * - Handles Delete/Backspace to remove last selected document
   * - Handles Enter to send message
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // First, let mention autocomplete handle the event
      if (mentionAutocompleteRef.current) {
        const handled = mentionAutocompleteRef.current.handleKeyDown(e);
        if (handled) return;
      }

      // Handle Delete/Backspace to remove last selected document when input is empty
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        !inputMessage.trim() &&
        selectedMinimalDocuments.length > 0
      ) {
        e.preventDefault();
        const lastDoc = selectedMinimalDocuments[selectedMinimalDocuments.length - 1];
        logger.info('[useInputHandlers] Removing last document via Delete/Backspace', {
          documentId: lastDoc.id,
          documentName: lastDoc.company_name || lastDoc.name,
        });
        deselectContextDocuments([lastDoc.id]);
        if (selectedDocuments.length === 1) {
          setContextFilters({});
        }
        return;
      }

      // Handle Enter to send message (Enter, Cmd+Enter, Ctrl+Enter all send; Shift+Enter = newline)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [
      inputMessage,
      selectedMinimalDocuments,
      selectedDocuments,
      mentionAutocompleteRef,
      deselectContextDocuments,
      setContextFilters,
      sendMessage,
    ]
  );

  /**
   * Handle click on document chip to open document selector
   */
  const handleDocumentChipClick = useCallback(
    (_documentName: string) => {
      if (mentionAutocompleteRef.current) {
        mentionAutocompleteRef.current.openDocumentSelector();
      }
    },
    [mentionAutocompleteRef]
  );

  /**
   * Check if the send button should be enabled
   */
  const canSend = inputMessage.trim().length > 0 || Object.keys(contextFilters).length > 0;

  return {
    handleKeyDown,
    handleDocumentChipClick,
    canSend,
  };
}
